import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { makePaginated } from '../../../../../adl-gen/common';
import { DbKey, makeWithDbId, WithDbId } from '../../../../../adl-gen/common/db';
import {
  makeInternationalized,
  makeSkill,
  Skill,
  TrainingOffering
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import {
  RepositoryEntries,
} from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { Route } from '../../../../../routing/app-routes';
import { SelectOption } from '../../../../../ui/widgets/select/select';
import {
  FieldState,
  newIntegerFieldState,
  newNonEmptyTextField
} from '../../../../../ui/widgets/text-input/text-field';
import * as FileUtility from '../../../../../util/file-repos-utility';
import { uniqueId } from '../../../../../util/utils';
import { SkillTrainingOfferingChange } from '../../../../stores';
import { SkillListAndData, SkillStore } from '../../../../stores/skill-store';
import {
  deleteHandler,
  I18nInput,
  I18nMarkdownEditorObserver,
  I18nTextInputObserver,
  renderButtons,
  SelectObserver,
  TextInputObserver
} from '../../../common/editor';
import { AdminApplicationRoleType } from '../../../common/list/list-types';
import { ListView } from '../../../common/list/list-view';
import { TrainingOfferingListEntry } from '../../lists/training-offering-lists/training-offering-list-view';
import { SkillToTrainingOfferingListView, UnassignedTrainingOfferingListView } from '../../lists/skill-lists/skill-to-training-offering-list-view';
import { SkillTrainingOfferingListEntry } from '../../lists/training-offering-lists/skill-training-offering-list-view';

const styles = require('../../../common/editor.css');

interface RestrictedFields {}

interface SkillItemState {
  name: I18nInput;
  definition: I18nInput;
  guidanceTips: I18nInput;
  ordering: FieldState<number>;
  competencyId: FieldState<string>;
}

interface SkillEditorProps extends WithI18nProps {
  debug?: boolean;
  item?: WithDbId<Skill>;
  data: SkillListAndData;
  store: SkillStore;

  onCreate(item: Skill): Promise<void>;
  onUpdate?(item: WithDbId<Skill>, updates: SkillTrainingOfferingChange[]): Promise<void>;
  onCancel(): void;
  applicationRole: AdminApplicationRoleType;
  navigateTo(route: Route): void;
}

/**
 * Component to maintain Skills.
 */
@observer
class SkillEditor0 extends React.Component<SkillEditorProps> {
  @observable fieldState: SkillItemState = {
    name: makeInternationalized({ en: newNonEmptyTextField() }),
    definition: makeInternationalized({ en: newNonEmptyTextField() }),
    guidanceTips: makeInternationalized({ en: newNonEmptyTextField() }),
    ordering: newIntegerFieldState(),
    competencyId: newNonEmptyTextField()
  };

  @observable assignedTrainingOfferings: Array<SkillTrainingOfferingChange> = [];

  // TODO if not used - remove
  restrictedFields: RestrictedFields = {};
  noShowFields = {};

  @observable lang: LangCode;

  @observable locked = false;

  constructor(props: Readonly<SkillEditorProps>) {
    super(props);
    const { initI18nEmpty: initEmpty, initI18nFromInput: initFromInput } = props;
    const state = this.fieldState;
    this.lang = props.currentLangCode();
    if (props.applicationRole !== 'super') {
      // TODO: enable later
      throw Error('Client admin cannot edit Skill: not authorized');
    }
    if (props.item) {
      const { id, value } = props.item;
      const { data } = props.data;
      initFromInput(state.name, value.name);
      initFromInput(state.definition, value.definition);
      initFromInput(state.guidanceTips, value.guidanceTips);
      state.ordering.onChange(`${value.ordering}`);
      state.competencyId.onChange(value.competencyId);
      this.assignedTrainingOfferings = data.availableTrainingOfferings
        // Pick up only the STOs associated with the skill
        .filter(sto => sto.value.skillId === id)
        .map(sto => ({
          kind: 'loaded',
          id: sto.id,
          skillId: id,
          trainingOfferingId: sto.value.trainingOfferingId,
          method: sto.value.method
        }));
    } else {
      state.name = initEmpty(state.name);
      state.definition = initEmpty(state.definition);
      state.guidanceTips = initEmpty(state.guidanceTips);
      state.ordering.onChange(`0`);
      state.competencyId.onChange('');
    }
  }

  @action lockUpdates = () => (this.locked = true);

  @action unlockUpdates = () => (this.locked = false);

  @action
  changeLang = (opt: SelectOption) => {
    this.lang = opt.value as LangCode;
  };

  @action
  changeCompetency = (opt: SelectOption) => {
    this.fieldState.competencyId.onChange(opt.value as string);
  };

  // There's only one guidanceTips folder - but may be more later (per client)
  // Also just keeping things consistent, and using consistent way of picking
  // repo name.
  getRepo = (): RepositoryEntries => {
    return FileUtility.getGuidanceRepo(this.props.data.repository);
  };

  LangSelector = observer(() => {
    const { defaultLang, i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    const defaultOption = options.find(opt => (opt.value as LangCode) === this.lang) || {
      label: defaultLang().name,
      value: defaultLang().code
    };
    return SelectObserver({
      options,
      selectedOption: defaultOption,
      title: 'Language',
      xsSpan: 2,
      onChange,
      disabled: false,
      debug: false
    });
  });

  NameObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.name,
      type: 'text',
      lang: this.lang,
      title: 'Name',
      placeholder: `Name of Skill`,
      xsSpan: 4,
      debug: this.props.debug
    })
  );

  DefinitionObserver = observer(() =>
    I18nTextInputObserver({
      field: this.fieldState.definition,
      type: 'textarea',
      lang: this.lang,
      title: 'Skill Definition',
      placeholder: `Define this Skill`,
      xsSpan: 4,
      debug: this.props.debug
    })
  );

  GuidanceTipsObserver = observer(() =>
    I18nMarkdownEditorObserver({
      field: this.fieldState.guidanceTips,
      title: 'Guidance Tips',
      lang: this.lang,
      repo: this.getRepo(),
      onUpload: this.props.store.uploadRepositoryEntry,
      xsSpan: 4
    })
  );

  OrderingObserver = observer(() =>
    TextInputObserver({
      field: this.fieldState.ordering,
      type: 'number',
      title: 'Order in lists',
      placeholder: '0...',
      xsSpan: 1,
      debug: this.props.debug
    })
  );

  CompetencyObserver = observer(() => {
    const { data, item, t } = this.props;
    const { competencies } = data.data;
    const options = competencies.map(c => ({ label: t(c.value.fullName, this.lang), value: c.id }));
    let selectedOption;
    if (item) {
      selectedOption = options.find(opt => opt.value === item.value.competencyId);
    }
    if (!selectedOption) {
      selectedOption = { label: 'None', value: '' };
    }
    return SelectObserver({
      options,
      selectedOption,
      title: 'Competency',
      xsSpan: 2,
      onChange: this.changeCompetency,
      disabled: false,
      debug: false
    });
  });

    @computed
  get currentUnassignedTrainingOfferings(): TrainingOfferingListEntry[] {
    const { applicationRole, data } = this.props;
    const { trainingOfferings } = data.data;
    return (
      trainingOfferings
        .map(tro => ({ id: tro.id, ...tro.value, identity: applicationRole }))
        // Remove skill already assigned
        .filter(tr => !this.assignedTrainingOfferings.map(sto => (sto.kind !== 'delete' ? sto.trainingOfferingId : undefined)).includes(tr.id))
    );
  }

  // Doesn't actually remove from unassigned list (doesn't exist), but just adds a 'create' to assigned list
  @action
  onRemoveTrainingOfferingFromUnassignedList = ({ id }: { id: DbKey<TrainingOffering> }) => {
    if (!this.props.item) return;
    const stoAlreadyExists = this.assignedTrainingOfferings.findIndex(
      sto => (sto.kind !== 'delete' && sto.trainingOfferingId === id)
    );
    if (stoAlreadyExists < 0) {
      this.props.item &&
        this.assignedTrainingOfferings.push({
          kind: 'create',
          id: `${uniqueId()}`,
          skillId: this.props.item.id,
          trainingOfferingId: id,
          method: { kind: 'relevance', value: 0 }
        });
    }
  };

  @action
  onDeleteSkillTrainingOffering = ({ id }: { id: string }) => {
    // Display is already updated in the ListView, but let's log the delete.
    const stoToDelete = this.assignedTrainingOfferings.findIndex(
      sto => (sto.kind !== 'delete' && sto.id === id)
    );
    if (stoToDelete >= 0) {
      // Note this automatically adds to unassigned
      if (this.assignedTrainingOfferings[stoToDelete].kind === 'create') 
        // Artifact item - just delete
        this.assignedTrainingOfferings.splice(stoToDelete, 1);
      else
        // Item loaded from database: delete, then add back as a 'delete'
        this.assignedTrainingOfferings.splice(stoToDelete, 1, { kind: 'delete', id });
    }
  };

  @action
  onUpdateSkillTrainingOffering = (fieldName: string, item: SkillTrainingOfferingListEntry) => (
    value: string | number
  ) => {
    const stoToUpdate = this.assignedTrainingOfferings.findIndex(
      sto => (sto.kind !== 'delete') && sto.id === item.id
    );
    if (stoToUpdate >= 0 && fieldName === 'method') {
      this.assignedTrainingOfferings.splice(stoToUpdate, 1, {
        // If it's a 'create', must remain as a CREATE or we'll try to write nonsense into the db
        kind: this.assignedTrainingOfferings[stoToUpdate].kind === 'create' ? 'create' : 'update',
        id: item.id,
        skillId: item.skillId,
        trainingOfferingId: item.trainingOfferingId,
        method: { kind: 'relevance', value: value as number }
      });
    }
  };

  onSelectTrainingOffering = async (id: DbKey<TrainingOffering>) => {
    // Drastic  - flips you straight out of skills and into the Training Offering page, dropping all changes
    // Todo - don't navigate, just pull T/O view into <dialog> component (maybe the T/O preview card?)
    this.props.navigateTo({
      route: 'admin',
      page: 'training-offering',
      id
    });
  }

  AssignedSkillTrainingOfferingsObserver = observer(() => {
    // Don't display for 'new' - we need a T/O ID and don't want to orchestrate that rn
    if (!this.props.item) {
      return null;
    }
    const { data } = this.props;
    const assignedWithoutDeletes = this.assignedTrainingOfferings.filter(tro => tro.kind !== 'delete');
    return (assignedWithoutDeletes.length > 0 ?
      <>
        <h5>Assigned Training Offerings and Relevance Values</h5>
        <ListView
          // Trigger proper re-render on change
          key={`${assignedWithoutDeletes.length}`}
          list={makePaginated({
            items: assignedWithoutDeletes,
            current_offset: 0,
            total_size: assignedWithoutDeletes.length
          })}
          fields={SkillToTrainingOfferingListView}
          refData={data.data}
          onDelete={this.onDeleteSkillTrainingOffering}
          onChange={this.onUpdateSkillTrainingOffering}
          onSelect={this.onSelectTrainingOffering}
        />
      </> : null
    );
  });

  UnassignedTrainingOfferingsObserver = observer(() => {
    // Don't display for 'new' - we need a T/O ID and don't want to orchestrate that rn
    if (!this.props.item) {
      return null;
    }
    return (
      <>
        <h5>Unassigned Training Offerings</h5>
        <ListView
          // Trigger proper re-render on change
          key={`${this.currentUnassignedTrainingOfferings.length}`}
          list={makePaginated({
            items: this.currentUnassignedTrainingOfferings,
            current_offset: 0,
            total_size: this.currentUnassignedTrainingOfferings.length
          })}
          fields={UnassignedTrainingOfferingListView}
          onDelete={this.onRemoveTrainingOfferingFromUnassignedList} // ... and add 'create' to assigned list
          onSelect={this.onSelectTrainingOffering}
        />
      </>
    );
  });



  @computed
  get inputIsValid(): boolean {
    const { data, isI18nValid } = this.props;
    const { competencies } = data.data;
    const { name, definition, guidanceTips, competencyId, ordering } = this.fieldState;
    return (
      isI18nValid(name) &&
      isI18nValid(definition) &&
      isI18nValid(guidanceTips) &&
      ordering.getValid() !== undefined &&
      competencyId.getValid() !== undefined &&
      competencies.find(c => c.id === competencyId.value()) !== undefined &&
      this.assignedTrainingOfferings.every(sto =>
        sto.kind !== 'delete' ? sto.method.kind === 'relevance' && sto.method.value >= 1 : true
      )

    );
  }

  onSave = async () => {
    const { item, makeI18nContent, onCreate, onUpdate } = this.props;

    runInAction(() => this.lockUpdates());

    /// Build from scratch here to get latest changes
    const s = makeSkill({
      name: makeI18nContent(this.fieldState.name),
      definition: makeI18nContent(this.fieldState.definition),
      guidanceTips: makeI18nContent(this.fieldState.guidanceTips),
      ordering: Number(this.fieldState.ordering.value()),
      competencyId: this.fieldState.competencyId.value(),
      /// --- [ TODO: Use this to track existing images in the guidanceTips. ] ---
      /// See "Resources management issue" comment in TrainingOfferingEditor.onDescriptionImageUpload
      imageUrls: []
    });

    if (item && onUpdate) {
      await onUpdate(
        makeWithDbId({
          id: item.id,
          value: { ...s }
        }), this.assignedTrainingOfferings
      );
    } else {
      await onCreate(s);
    }

    runInAction(() => this.unlockUpdates());

  };

  // TODO: At appropriate time add this.props.role === "super" guards and use restrictedFields if required
  Edit = () => (
    <React.Fragment>
      <h4>{this.props.item ? 'Update' : 'New'} Skill</h4>
      <div className={styles.row}>
        <this.LangSelector />
      </div>
      <div className={styles.row}>
        <this.CompetencyObserver />
      </div>
      <div className={styles.row}>
        <this.NameObserver />
        <this.OrderingObserver />
      </div>
      <div className={styles.row}>
        <this.DefinitionObserver />
      </div>
      <div className={styles.row}>
        <this.GuidanceTipsObserver />
      </div>
      <div className={styles.row}>
        <this.AssignedSkillTrainingOfferingsObserver />
      </div>
      <div className={styles.row}>
        <this.UnassignedTrainingOfferingsObserver />
      </div>
    </React.Fragment>
  );

  render() {
    return (
      // {/* TODO aria (or DOM) labels */}
      <div className={styles.page}>
        <div className={styles.body}>
          <div>
            {renderButtons(
              this.onSave,
              undefined,
              this.props.onCancel,
              () => this.inputIsValid && !this.locked,
              undefined,
              () => !this.locked
            )}
          </div>
          <this.Edit />
        </div>
      </div>
    );
  }
}

export interface SkillDeleterProps {
  items?: WithDbId<Skill>[];
  onDelete?(ids: DbKey<Skill>[]): Promise<void>;
  onCancel(): void;
}

/** Base component to create/update Competency */
// @observer
export class SkillDeleter extends React.Component<SkillDeleterProps> {
  onDelete = async () => {
    const { items, onDelete } = this.props;
    if (items && onDelete) {
      await onDelete(items.map(item => item.id));
    }
  };

  render() {
    const { onCancel, items } = this.props;
    return items ? (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            {deleteHandler(this.onDelete, onCancel, 'Skills', items.map(item => item.value.name.value.en))}
          </div>
        </div>
      </div>
    ) : null;
  }
}

export const SkillEditor = withI18n(SkillEditor0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/skill-editor/skill-editor.tsx